/**
 * faqMatch.js — filtre de pertinence pour la recherche de réponses FAQ.
 *
 * POURQUOI CE FILTRE EXISTE
 * Fuse.js compare des suites de caractères, pas du sens. Sur un corpus de
 * questions rédigées en français, l'échafaudage interrogatif (« quel est le
 * meilleur… », « comment… ») suffit à faire matcher n'importe quoi : la requête
 * « quel est le meilleur restaurant de sushi à Tokyo » remontait « Quel
 * opérateur a le meilleur réseau ? ».
 *
 * Un seuil de score ne peut PAS régler ça — mesuré sur le corpus réel : une
 * requête pertinente score 0.900 et une requête hors-sujet 0.899. Les deux
 * distributions se chevauchent complètement.
 *
 * On exige donc un recoupement LEXICAL : au moins un mot porteur de sens de la
 * requête doit apparaître dans la question ou la réponse. Déterministe,
 * explicable, et ça coûte un parcours de chaîne.
 *
 * Conséquence assumée : le moteur se tait plus souvent. C'est le comportement
 * voulu — mieux vaut « aucune réponse sourcée » qu'une réponse à côté affichée
 * avec l'aplomb d'une vraie.
 */

// Mots vides + échafaudage interrogatif des trois langues. « meilleur / best /
// mejor » en font partie : ils portent l'intention comparative, pas le SUJET.
const STOPWORDS = new Set([
  // fr
  'quel', 'quelle', 'quels', 'quelles', 'que', 'quoi', 'qui', 'est', 'sont', 'suis',
  'comment', 'pourquoi', 'quand', 'combien', 'faut', 'faut-il', 'puis', 'puis-je',
  'dois', 'dois-je', 'peut', 'peut-on', 'pour', 'avec', 'sans', 'dans', 'sur', 'les',
  'des', 'une', 'mon', 'mes', 'ton', 'ses', 'son', 'leur', 'plus', 'moins', 'meilleur',
  'meilleure', 'bien', 'tout', 'tous', 'cette', 'cet', 'aux', 'par', 'pas', 'ne',
  // en
  'what', 'which', 'how', 'why', 'when', 'where', 'much', 'many', 'can', 'should',
  'the', 'and', 'for', 'with', 'without', 'best', 'good', 'from', 'into', 'about',
  'does', 'you', 'your', 'are', 'is', 'do', 'to', 'of', 'in', 'on', 'it',
  // es
  'cual', 'cuales', 'como', 'porque', 'cuando', 'cuanto', 'cuanta', 'donde', 'puedo',
  'debo', 'para', 'con', 'sin', 'los', 'las', 'una', 'mejor', 'buena', 'bueno',
  'mas', 'menos', 'todo', 'todos', 'esta', 'este', 'por', 'que', 'del', 'al',
]);

/** Minuscules sans accents — « cédula » et « cedula » doivent se rencontrer. */
export function normalize(str) {
  return (str || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9\s-]/g, ' ');
}

/**
 * Mots porteurs de sens d'une requête : ≥ 4 caractères et hors liste de mots
 * vides. Le seuil de 4 écarte « le / la / des » sans écarter « visa », « chien »,
 * « ITBMS ».
 */
export function contentTokens(query) {
  return [...new Set(normalize(query).split(/[\s-]+/).filter((w) => w.length >= 4 && !STOPWORDS.has(w)))];
}

/** Le mot (ou son radical tronqué) apparaît-il dans ce texte ? */
function hits(haystack, tok) {
  if (haystack.includes(tok)) return true;
  // Préfixe : « banque » retrouve « bancaire », « importer » retrouve « import ».
  const stem = tok.slice(0, Math.max(4, tok.length - 2));
  return stem.length >= 4 && haystack.includes(stem);
}

/**
 * Une entrée FAQ est-elle vraiment pertinente pour cette requête ?
 *
 * Deux voies d'admission, volontairement asymétriques :
 *   · UN mot porteur dans la QUESTION suffit — c'est elle qui porte l'intention ;
 *   · dans la seule RÉPONSE, il en faut DEUX.
 *
 * L'asymétrie vient d'un cas réel : « quel est le meilleur ordinateur portable »
 * remontait « Quel est le meilleur mois pour visiter le Panama ? », parce que le
 * mot « portable » traînait quelque part dans le corps de la réponse. Un mot
 * croisé au fond d'un paragraphe est une coïncidence ; deux, c'est un sujet.
 */
export function isRelevant(entry, tokens) {
  if (!tokens.length) return false;
  const question = normalize(entry.question);
  if (tokens.some((tok) => hits(question, tok))) return true;
  const answer = normalize(entry.answer);
  return tokens.filter((tok) => hits(answer, tok)).length >= 2;
}

/**
 * Recherche complète : filtre PUIS classe. Source unique pour la modale de
 * recherche et la page /questions.
 *
 * ⚠️ L'ORDRE EST LA PERFORMANCE. La version initiale laissait Fuse parcourir
 * les ~390 réponses puis filtrait sa sortie : mesuré à **40,8 ms par caractère
 * tapé** sur une machine de dev — 2,5× le budget d'une frame de 16 ms, et un
 * mobile est 4 à 6 fois plus lent. Le coupable était Fuse (32 ms), pas le
 * filtre (2,8 ms) : le fuzzy sur des réponses longues coûte cher.
 *
 * En filtrant d'abord — de simples comparaisons de chaînes — Fuse ne classe
 * plus que ~24 candidats au lieu de 390 : **5,9 ms par frappe, soit 7× moins,
 * pour des résultats identiques** (vérifié : même liste, même ordre).
 *
 * Fuse est reconstruit à chaque appel sur le petit sous-ensemble ; indexer 24
 * entrées coûte une fraction de milliseconde, très en dessous de ce qu'on
 * économise.
 */
export function searchFaq(FuseCtor, faqs, query, limit = 3) {
  const q = (query ?? '').trim();
  if (q.length < 2 || !faqs?.length) return [];
  const tokens = contentTokens(q);
  if (!tokens.length) return [];

  const candidats = faqs.filter((e) => isRelevant(e, tokens));
  if (!candidats.length) return [];

  return new FuseCtor(candidats, {
    threshold: 0.4,
    ignoreLocation: true,
    minMatchCharLength: 2,
    keys: [{ name: 'question', weight: 0.75 }, { name: 'answer', weight: 0.25 }],
  })
    .search(q)
    .slice(0, limit)
    .map((r) => r.item);
}
